import { REPORT, REPORT_RANGE } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, countUnique, sum } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { lagDateRange, createRowsFromGroupedDates, groupByKeys, getUniqueDates, getValues, fixedDateRange } from '@utils/data';
import { useMemo } from 'react';
import { styleDateRange } from '@utils/styles';

interface MonthlyCutVolumeGrowthProps {
    dateRange: { startDate: string; endDate: string };
}

export const MonthlyCutVolumeGrowth = ({ dateRange }: MonthlyCutVolumeGrowthProps) => {
    const fixedDate = fixedDateRange(dateRange);

    const adjustedRange = useMemo(() => lagDateRange(fixedDate, 3), [fixedDate]);

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.RESELLER_CUT_VOLUME_GROWTH_BY_RESELLER,
        params: { ...adjustedRange, range: 'monthly' },
    });

    const groupedData = useMemo(() => {
        const groupedByReseller = groupByKeys(data, ['resellerName', 'date']);
        const dateKeys = getUniqueDates(data, REPORT_RANGE.MONTHLY);
        return createRowsFromGroupedDates(groupedByReseller, dateKeys, 3);
    }, [data]);

    return (
        <BaseReport
            title={'Monthly Cut Volume Growth by Reseller'}
            description={
                'This table allows you to see the number of cuts for for a 4 month period by Reseller, along with the % growth from the previous month for each, so you can identify Resellers who are growing or shrinking. Note that if you select more than 1 month in the date range above, each Reseller will have multiple rows (one for each full month).'
            }
            rangeText={styleDateRange([fixedDate.startDate, fixedDate.endDate])}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: [
                    'Reseller Name',
                    'Month',
                    'Cuts selected Month',
                    '% Growth',
                    'Cuts 1 Month Prior',
                    '% Growth',
                    'Cuts 2 Months Prior',
                    '% Growth',
                    'Cuts 3 Months Prior',
                    '% Growth',
                ],
                data: groupedData.map((items) => [
                    items[0]?.resellerName || 'Error: Could Not Find',
                    items[1]?.date || 'Error: Could Not Find',
                    ...getValues(items.slice(1), ['successfulCuts', 'growthPct']),
                ]),
                summaries: [countUnique, countUnique, sum, average, sum, average, sum, average, sum, average],
                sort: [
                    basicSort,
                    dateSort(REPORT_RANGE.MONTHLY),
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                    basicSort,
                ],
            }}
        />
    );
};
